import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AbstractComponent } from 'lib-trend-core';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ChatConnectionService } from '../state/chat-connection.service';
import { connectionRestoredSelector, connectionStatusSelector } from '../state/chat.selectors';

/** What the banner renders — `null` hides it entirely. */
export type ChatConnectionBannerState = 'offline' | 'reconnecting' | 'restored' | null;

/**
 * Internal Chat connection banner (SPEC-2026-07-internal-chat, T-25 · RNF-08).
 *
 * Thin bar mounted at the top of every chat surface (the `/chat` workspace and
 * the bubble popup). It renders nothing while the link is healthy, so the host
 * layouts only need one line and no conditional of their own:
 *
 * - link down, device offline → amber "Conexão perdida";
 * - link down, device online  → amber "Reconectando…" with a spinner;
 * - just recovered           → brief green "Conectado" (auto-hidden after
 *   CHAT_CONNECTION_RESTORED_MS by ChatConnectionService).
 *
 * The state itself comes from the `chatState` ui slice, fed by
 * ChatConnectionService (activated here, idempotently). Colors are Bootstrap
 * contextual classes; the slide-in is disabled under `prefers-reduced-motion`.
 */
@Component({
  selector: 'chat-connection-banner',
  templateUrl: './chat-connection-banner.component.html',
  styleUrls: ['./chat-connection-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatConnectionBannerComponent extends AbstractComponent implements OnInit {

  /** Single stream so the template subscribes once (no double async pipes). */
  readonly bannerState$: Observable<ChatConnectionBannerState> = combineLatest([
    this.store.select(connectionStatusSelector),
    this.store.select(connectionRestoredSelector),
  ]).pipe(
    map(([status, restored]): ChatConnectionBannerState => {
      if (status === 'offline') return 'offline';
      if (status === 'reconnecting') return 'reconnecting';
      return restored ? 'restored' : null;
    }),
    distinctUntilChanged(),
  );

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly chatConnection: ChatConnectionService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    // Idempotent — the first chat surface that mounts starts the watcher.
    this.chatConnection.init();
  }
}
